import React from 'react';
import { useCheckLicenseQuery } from '../../../redux/apis/endpoints/licensingApi';
import { useSelector } from 'react-redux';
import Button from '../../../components/ui/Button';

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="M22 4L12 14.01l-3-3" />
  </svg>
);

const DigitalLicenseSection = ({
  product,
  onOpenVTO,
  isVTOAdding,
  hasExistingTryOns,
  onPurchaseLicense
}) => {
  const token = useSelector(state => state.auth?.token);
  const isLoggedIn = !!token;
  

  const licensingEnabled = product?.licensingEnabled;

  const { data: licenseCheck } = useCheckLicenseQuery(
    { listingId: product?.id, itemType: 'product' },
    { skip: !isLoggedIn || !licensingEnabled || !product?.id }
  );
  const hasLicense = licenseCheck?.hasLicense || false;

  if (!product) return null;

  const { licensingType, licensingPrice, licensingTerms } = product;

  const isFree = licensingType === 'free';
  const isPaid = licensingEnabled && !isFree;

  const currency = product.currency || 'USD';
  const formattedPrice = isPaid && licensingPrice
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(licensingPrice)
    : null;

  const termsLabel = {
    per_use: 'per use',
    monthly: 'monthly',
    unlimited: 'one-time'
  }[licensingTerms] || 'one-time';

  return (
    <div className="flex flex-col gap-2.5 pt-4 border-t border-gray-200">
      {/* Try-on row */}
      <div className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="text-brand-teal flex-shrink-0">
            <SparkleIcon />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">Virtual try-on</div>
            <div className="text-xs text-gray-500">
              1 credit{isPaid ? ' · watermarked preview' : ' · ~30s'}
            </div>
          </div>
        </div>
        <Button
          variant="teal"
          size="sm"
          style={{ minWidth: 100 }}
          onClick={onOpenVTO}
          disabled={isVTOAdding}
          isLoading={isVTOAdding}
        >
          {hasExistingTryOns ? 'View' : 'Try on'}
        </Button>
      </div>

      {/* License card */}
      {licensingEnabled && isPaid && formattedPrice && (
        <div className={`flex items-center justify-between gap-3 p-3 border rounded-lg ${
          hasLicense ? 'bg-teal-50 border-teal-200' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`flex-shrink-0 ${hasLicense ? 'text-emerald-600' : 'text-gray-500'}`}>
              {hasLicense ? <CheckCircleIcon /> : <LockIcon />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">
                {hasLicense ? 'Licensed' : 'Digital license'}
              </div>
              <div className="text-xs text-gray-500 leading-relaxed">
                {hasLicense
                  ? 'You own the license. Clean full-res images available.'
                  : `Clean full-res image · ${termsLabel}`}
              </div>
            </div>
          </div>
          {!hasLicense && (
            <Button
              variant="teal-outline"
              size="sm"
              style={{ minWidth: 100 }}
              onClick={() => onPurchaseLicense && onPurchaseLicense(product)}
            >
              License {formattedPrice}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DigitalLicenseSection;
